import React, { type JSX } from "react";
import type { PaginationInfo } from "../types";

const PAGE_BUTTON = "relative inline-flex items-center px-4 py-2 border text-sm font-medium";
const CURRENT_PAGE = "z-10 bg-blue-50 border-blue-500 text-blue-600";
const OTHER_PAGE = "bg-white border-gray-300 text-gray-500 hover:bg-gray-50";
const ELLIPSIS =
  "relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700";
const STEP_BUTTON =
  "relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed";

/** Gap before the current page's neighbours. */
const LEADING_ELLIPSIS_AFTER = 4;
/** Gap after them. */
const TRAILING_ELLIPSIS_BEFORE = 3;

/** A numbered page button. */
function PageButton({
  page,
  current,
  onPageChange,
}: {
  page: number;
  current: number;
  onPageChange: (page: number) => void;
}): JSX.Element {
  return (
    <button
      onClick={() => onPageChange(page)}
      className={`${PAGE_BUTTON} ${page === current ? CURRENT_PAGE : OTHER_PAGE}`}
    >
      {page}
    </button>
  );
}

/** First page, last page, the current page's neighbours, and ellipses between. */
function pageNumbers(
  current: number,
  total: number,
  onPageChange: (page: number) => void
): JSX.Element[] {
  const pages: JSX.Element[] = [];

  // Always show first page
  if (total > 1) {
    pages.push(<PageButton key={1} page={1} current={current} onPageChange={onPageChange} />);
  }

  if (current > LEADING_ELLIPSIS_AFTER) {
    pages.push(
      <span key="ellipsis1" className={ELLIPSIS}>
        ...
      </span>
    );
  }

  // Add pages around current page
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let page = start; page <= end; page++) {
    if (page !== 1 && page !== total) {
      pages.push(<PageButton key={page} page={page} current={current} onPageChange={onPageChange} />);
    }
  }

  if (current < total - TRAILING_ELLIPSIS_BEFORE) {
    pages.push(
      <span key="ellipsis2" className={ELLIPSIS}>
        ...
      </span>
    );
  }

  // Always show last page (if more than 1 page)
  if (total > 1) {
    pages.push(
      <PageButton key={total} page={total} current={current} onPageChange={onPageChange} />
    );
  }

  return pages;
}

/** Previous / Next only, for narrow screens. */
function CompactPager({
  pagination,
  onPageChange,
}: {
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
}): JSX.Element {
  return (
    <div className="flex-1 flex justify-between sm:hidden">
      <button
        onClick={() => onPageChange(pagination.page - 1)}
        disabled={!pagination.has_prev}
        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Previous
      </button>
      <button
        onClick={() => onPageChange(pagination.page + 1)}
        disabled={!pagination.has_next}
        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Next
      </button>
    </div>
  );
}

/** "Showing x to y of z results". */
function ResultRange({ pagination }: { pagination: PaginationInfo }): JSX.Element {
  return (
    <p className="text-sm text-gray-700">
      Showing{" "}
      <span className="font-medium">{(pagination.page - 1) * pagination.page_size + 1}</span> to{" "}
      <span className="font-medium">
        {Math.min(pagination.page * pagination.page_size, pagination.total_items)}
      </span>{" "}
      of <span className="font-medium">{pagination.total_items}</span> results
    </p>
  );
}

/** Pager shown below the conversation list; hidden on a single page. */
export default function ConversationsPagination({
  pagination,
  onPageChange,
}: {
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
}): JSX.Element {
  return (
    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-b-lg">
      <CompactPager pagination={pagination} onPageChange={onPageChange} />
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <ResultRange pagination={pagination} />
        </div>
        <div>
          <nav
            className="relative z-0 inline-flex rounded-md shadow-xs -space-x-px"
            aria-label="Pagination"
          >
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={!pagination.has_prev}
              className={`${STEP_BUTTON} rounded-l-md`}
            >
              Previous
            </button>

            {pageNumbers(pagination.page, pagination.total_pages, onPageChange)}

            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={!pagination.has_next}
              className={`${STEP_BUTTON} rounded-r-md`}
            >
              Next
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}
